import {
  useAttachmentDownloadUrl,
  useCompleteAttachment,
  useDeleteAttachment,
  usePresignAttachment,
  useTicketAttachments,
} from '@servicetim/ui/api';
import { Button, describe, ProblemBanner, toast, useConfirm } from '@servicetim/ui/components';
import { cn } from '@servicetim/ui/lib';
import { Download, LoaderCircle, Paperclip, RotateCcw, Trash2, Upload, X } from 'lucide-react';
import { type DragEvent, useId, useMemo, useReducer, useRef, useState } from 'react';
import { formatBytes, formatDateTime } from '../format';
import { RailCard } from '../rail-card';
import { useTicketContext } from '../ticket-context';
import {
  isUploadActive,
  runUpload,
  type UploadDeps,
  type UploadItem,
  uploadReducer,
  validateFile,
  xhrUpload,
} from './upload-machine';

const newUploadId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

/** Attachments: dropzone → presign → direct S3 POST with progress → complete; list, download, delete. */
export function AttachmentsCard() {
  const { ticket, meId, userLabel } = useTicketContext();
  const attachments = useTicketAttachments(ticket.id);
  const presign = usePresignAttachment(ticket.id);
  const complete = useCompleteAttachment(ticket.id);
  const remove = useDeleteAttachment(ticket.id);
  const downloadUrl = useAttachmentDownloadUrl();
  const confirm = useConfirm();
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const files = useRef(new Map<string, File>());
  const [uploads, dispatch] = useReducer(uploadReducer, []);
  const [dragging, setDragging] = useState(false);

  const deps = useMemo<UploadDeps>(
    () => ({
      presign: (file) =>
        presign.mutateAsync({
          filename: file.name,
          contentType: file.type || 'application/octet-stream',
          sizeBytes: file.size,
          commentId: null,
        }),
      upload: xhrUpload,
      complete: (attachmentId) => complete.mutateAsync(attachmentId),
    }),
    [presign.mutateAsync, complete.mutateAsync],
  );

  const start = (id: string, file: File) => {
    const problem = validateFile(file);
    if (problem) {
      dispatch({ type: 'failed', id, error: problem });
      return;
    }
    void runUpload(id, file, deps, dispatch).then((ready) => {
      if (ready) toast.success(`Attached ${ready.filename}`);
    });
  };

  const addFiles = (list: FileList | File[] | null) => {
    if (!list) return;
    for (const file of Array.from(list)) {
      const id = newUploadId();
      files.current.set(id, file);
      dispatch({ type: 'added', id, file: { name: file.name, size: file.size, type: file.type } });
      start(id, file);
    }
  };

  const retry = (item: UploadItem) => {
    const file = files.current.get(item.id);
    if (!file) return;
    dispatch({ type: 'retry', id: item.id });
    start(item.id, file);
  };

  const onDrop = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    setDragging(false);
    addFiles(event.dataTransfer.files);
  };

  const onDelete = (id: string, filename: string) =>
    confirm({
      title: `Delete ${filename}?`,
      description: 'The file is removed from storage and from this ticket.',
      confirmLabel: 'Delete',
      destructive: true,
      onConfirm: async () => {
        await remove.mutateAsync(id);
        toast.success('Attachment deleted');
      },
    });

  const items = attachments.items;
  return (
    <RailCard
      title={`Attachments${attachments.isSuccess ? ` (${items.length}${attachments.hasMore ? '+' : ''})` : ''}`}
      actions={
        <Button size="sm" variant="ghost" onClick={() => inputRef.current?.click()}>
          <Upload />
          Add files
        </Button>
      }
    >
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        multiple
        className="sr-only"
        aria-label="Add files"
        onChange={(e) => {
          addFiles(e.target.files);
          e.target.value = '';
        }}
      />
      <section
        aria-label="Drop files here"
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={cn(
          'rounded-md border border-dashed p-3 text-center text-xs transition-colors',
          dragging
            ? 'border-ring bg-accent-soft text-accent'
            : 'border-border-strong text-fg-muted',
        )}
      >
        Drop files here or{' '}
        <label htmlFor={inputId} className="cursor-pointer text-accent underline">
          browse
        </label>
        . Up to 25 MB each.
      </section>

      {uploads.length > 0 ? (
        <ul className="space-y-2" aria-label="Uploads">
          {uploads.map((u) => (
            <li key={u.id} className="space-y-1 rounded-md border border-border p-2">
              <div className="flex items-center justify-between gap-2">
                <span className="truncate" title={u.file.name}>
                  {u.file.name}
                </span>
                <span className="shrink-0 text-fg-muted text-xs">
                  {u.status === 'presigning'
                    ? 'Preparing…'
                    : u.status === 'uploading'
                      ? `${Math.round(u.progress * 100)}%`
                      : u.status === 'completing'
                        ? 'Finishing…'
                        : u.status === 'done'
                          ? 'Done'
                          : 'Failed'}
                </span>
              </div>
              {isUploadActive(u) ? (
                <progress
                  aria-label={`Uploading ${u.file.name}`}
                  value={u.progress}
                  max={1}
                  className="h-1.5 w-full overflow-hidden rounded-full [&::-webkit-progress-bar]:bg-surface-sunken [&::-webkit-progress-value]:bg-brand-600"
                />
              ) : null}
              {u.status === 'error' ? (
                <div className="flex items-center justify-between gap-2">
                  <p role="alert" className="text-red-600 dark:text-red-400 text-xs">
                    {u.error}
                  </p>
                  <span className="flex shrink-0 gap-1">
                    <Button size="sm" variant="ghost" onClick={() => retry(u)}>
                      <RotateCcw />
                      Retry
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="size-7"
                      aria-label="Dismiss"
                      onClick={() => dispatch({ type: 'dismissed', id: u.id })}
                    >
                      <X />
                    </Button>
                  </span>
                </div>
              ) : null}
              {u.status === 'done' ? (
                <div className="flex justify-end">
                  <Button
                    size="icon"
                    variant="ghost"
                    className="size-7"
                    aria-label="Dismiss"
                    onClick={() => dispatch({ type: 'dismissed', id: u.id })}
                  >
                    <X />
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      {attachments.error ? <ProblemBanner error={attachments.error} size="sm" /> : null}
      {attachments.isPending ? (
        <LoaderCircle className="size-4 animate-spin text-fg-subtle" />
      ) : null}
      {attachments.isSuccess && items.length === 0 ? (
        <p className="text-fg-muted">No attachments yet.</p>
      ) : null}
      <ul className="space-y-2">
        {items.map((a) => (
          <li key={a.id} className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <a
                href={downloadUrl(a.id)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex max-w-full items-center gap-1 text-accent hover:underline"
                title={`Download ${a.filename}`}
              >
                <Paperclip className="size-3.5 shrink-0" aria-hidden="true" />
                <span className="truncate">{a.filename}</span>
                <Download className="size-3.5 shrink-0" aria-hidden="true" />
              </a>
              <p className="text-fg-muted text-xs">
                {formatBytes(a.sizeBytes)} · {userLabel(a.uploaderId) ?? 'someone'} ·{' '}
                {formatDateTime(a.createdAt)}
                {a.status === 'pending' ? ' · upload incomplete' : ''}
              </p>
            </div>
            {a.uploaderId === meId || ticket.canEdit ? (
              <Button
                size="icon"
                variant="ghost"
                className="size-7 shrink-0"
                aria-label={`Delete ${a.filename}`}
                disabled={remove.isPending}
                onClick={() =>
                  void onDelete(a.id, a.filename).catch((e) => toast.error(describe(e).title))
                }
              >
                <Trash2 />
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
      {attachments.hasMore ? (
        <Button
          size="sm"
          variant="outline"
          onClick={attachments.loadMore}
          disabled={attachments.isLoadingMore}
        >
          Load more
        </Button>
      ) : null}
    </RailCard>
  );
}
